import { createContext, useContext, useReducer, ReactNode, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { User } from '../types'
import { apiClient } from '../api/client'
import { setPostLoginRedirect } from '../utils/postLoginRedirect'

export type AuthStatus = 'checking' | 'authenticated' | 'unauthenticated'

interface AuthState {
  user: User | null
  status: AuthStatus
  error: string | null
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'CHECK_DONE'; payload: User | null }
  | { type: 'LOGIN_SUCCESS'; payload: User }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }

const initialState: AuthState = {
  user: null,
  // A refresh always begins with the /me session check; stay 'checking'
  // until it resolves so protected routes do not render prematurely.
  status: 'checking',
  error: null,
}

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, error: null }
    case 'CHECK_DONE':
      return action.payload
        ? { user: action.payload, status: 'authenticated', error: null }
        : { user: null, status: 'unauthenticated', error: null }
    case 'LOGIN_SUCCESS':
      return { user: action.payload, status: 'authenticated', error: null }
    case 'LOGIN_FAILURE':
      return { user: null, status: 'unauthenticated', error: action.payload }
    case 'LOGOUT':
      return { user: null, status: 'unauthenticated', error: null }
    default:
      return state
  }
}

interface AuthContextType {
  user: User | null
  status: AuthStatus
  error: string | null
  loginWithCode: (code: string, redirectUri: string, codeVerifier?: string) => Promise<void>
  logout: () => void
  setError: (error: string) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState)
  const navigate = useNavigate()

  // Handle session-expiry events from the API interceptor (401 → refresh failed).
  useEffect(() => {
    const handleAuthExpired = (e: Event) => {
      const message = (e as CustomEvent).detail || 'Session expired. Please sign in again.'
      dispatch({ type: 'LOGIN_FAILURE', payload: message })
      // We navigate directly (bypassing <ProtectedRoute>), so record the
      // target ourselves — otherwise re-login couldn't return the user here.
      const path = window.location.pathname
      if (path !== '/login' && path !== '/auth/callback') {
        setPostLoginRedirect(path + window.location.search)
      }
      navigate('/login')
    }
    window.addEventListener('auth:expired', handleAuthExpired)
    return () => window.removeEventListener('auth:expired', handleAuthExpired)
  }, [navigate])

  // Restore session from the httpOnly session cookie. The app stays 'checking'
  // until this resolves; a failure is an explicit terminal state (no swallow).
  useEffect(() => {
    apiClient.getCurrentUser()
      .then((user) => dispatch({ type: 'CHECK_DONE', payload: user ?? null }))
      .catch(() => dispatch({ type: 'CHECK_DONE', payload: null }))
  }, [])

  /** Complete OIDC login: exchange the code for a Mana session (sets cookie). */
  const loginWithCode = async (code: string, redirectUri: string, codeVerifier?: string) => {
    dispatch({ type: 'LOGIN_START' })
    try {
      const { user } = await apiClient.exchangeAuthorizationCode(code, redirectUri, codeVerifier)
      dispatch({ type: 'LOGIN_SUCCESS', payload: user })
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Authentication failed'
      dispatch({ type: 'LOGIN_FAILURE', payload: errorMessage })
      throw new Error(errorMessage)
    }
  }

  const logout = () => {
    // Server clears the session cookies; no client-side token to remove.
    apiClient.logout().catch(() => {})
    dispatch({ type: 'LOGOUT' })
  }

  const setError = (error: string) => {
    dispatch({ type: 'LOGIN_FAILURE', payload: error })
  }

  const value: AuthContextType = {
    user: state.user,
    status: state.status,
    error: state.error,
    loginWithCode,
    logout,
    setError,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
